import React, { useEffect, useState } from 'react';
import { Box, Typography, LinearProgress, CircularProgress } from '@mui/material';
import { AccessTime as TimeIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { timesheetService } from '../../../services/timesheetService';
import { TimesheetSummary } from '../../../types';
import { useAuth } from '../../../contexts/AuthContext';

const MyTimesheetWidget: React.FC = () => {
    const { user } = useAuth();
    const [summary, setSummary] = useState<TimesheetSummary | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchTimesheet = async () => {
            try {
                // Calculate current week start (Monday)
                const now = new Date();
                const day = now.getDay();
                const diff = now.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
                const monday = new Date(now.setDate(diff));
                const weekStart = monday.toISOString().split('T')[0];

                const data = await timesheetService.getTimesheetSummary(weekStart, user?.id);
                setSummary(data);
                setLoading(false);
            } catch (err) {
                console.error('Failed to fetch timesheet summary', err);
                setError('Failed to load timesheet');
                setLoading(false);
            }
        };

        if (user) {
            fetchTimesheet();
        }
    }, [user]);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={24} /></Box>;
    }

    if (error) {
        return <Typography color="error" variant="body2">{error}</Typography>;
    }

    if (!summary) {
        return <Typography variant="body2" color="text.secondary">No data available.</Typography>;
    }

    const totalHours = summary.grand_total || 0;
    const targetHours = summary.weekly_hours ?? user?.weekly_hours ?? 40;
    const progress = targetHours > 0 ? Math.min((totalHours / targetHours) * 100, 100) : 0;

    // Color logic: < targetHours is warning/info, >= targetHours is success
    const progressColor = totalHours >= targetHours ? 'success' : 'primary';

    return (
        <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <TimeIcon color="action" fontSize="large" />
                <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                        {totalHours} <Typography component="span" variant="body2" color="text.secondary">/ {targetHours} h</Typography>
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Logged this week
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ width: '100%' }}>
                <LinearProgress
                    variant="determinate"
                    value={progress}
                    color={progressColor}
                    sx={{ height: 10, borderRadius: 5 }}
                />
            </Box>
        </Box>
    );
};

export default MyTimesheetWidget;
